import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    closeBatch,
    createBatch,
    fetchOptions,
    fetchStats,
    fetchStatus,
    uploadFile,
} from '../api/transcription';
import FileQueue from '../components/FileQueue';
import StatGrid from '../components/StatGrid';
import StatusPill from '../components/StatusPill';
import Toast from '../components/Toast';
import useSessionState from '../hooks/useSessionState';
import { TRANSCRIPTION_POLL_MS } from '../config';
import { formatBytes, formatHours, formatNumber, formatPercent, formatTime } from '../utils/format';

const USAGE_KEY = 'lfo.transcription.usage';
const EMPTY_USAGE = { uploads: 0, bytes: 0, lastAt: null };

// Die API nimmt den Namen als freien String entgegen; hier bleibt es bewusst bei
// unproblematischen Zeichen, weil daraus ein E-Mail-Anhang gebaut wird.
const NAME_PATTERN = /^[A-Za-z0-9_-]+$/;

/** Zustandsnamen aus /status.jobs - unbekannte Keys werden unveraendert gezeigt. */
const JOB_STATE_LABELS = {
    rejected: 'Abgelehnt',
    queued: 'In Warteschlange',
    running: 'Laufend',
    done: 'Fertig',
    failed: 'Fehlgeschlagen',
};

const UMLAUTS = { ä: 'ae', ö: 'oe', ü: 'ue', Ä: 'Ae', Ö: 'Oe', Ü: 'Ue', ß: 'ss' };

/**
 * Dateinamen-Vorschlag aus dem Originalnamen.
 * Umlaute werden umschrieben statt entfernt, sonst wird aus "Gespräch" ein
 * "Gesprch". Verbleibende Sonderzeichen werden zu Unterstrichen.
 */
function suggestName(fileName) {
    return fileName
        .replace(/\.[^.]+$/, '')
        .replace(/[äöüÄÖÜß]/g, (char) => UMLAUTS[char])
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // sonstige Akzente (é, ç, …)
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40);
}

let entryCounter = 0;
const makeEntry = (file) => ({
    id: `f${(entryCounter += 1)}`,
    file,
    name: suggestName(file.name),
    state: 'pending',
    error: null,
});

function nameErrorFor(value) {
    if (!value) return 'Bitte einen Namen angeben.';
    if (!NAME_PATTERN.test(value)) return 'Erlaubt sind Buchstaben, Ziffern, Bindestrich und Unterstrich.';
    return '';
}

function TranscriptionTool() {
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [stats, setStats] = useState(null);
    const [options, setOptions] = useState(null);
    const [optionsError, setOptionsError] = useState(null);
    const [sessionUsage, setSessionUsage] = useSessionState(USAGE_KEY, EMPTY_USAGE);

    // 'single' = eine Datei, 'batch' = Sammelauftrag
    const [mode, setMode] = useState('single');
    const [file, setFile] = useState(null);
    const [attachmentName, setAttachmentName] = useState('');
    const [entries, setEntries] = useState([]);
    const [batchLabel, setBatchLabel] = useState('');
    const [batchResult, setBatchResult] = useState(null);

    const [email, setEmail] = useState('');
    const [outputType, setOutputType] = useState('');
    const [language, setLanguage] = useState('');
    const [model, setModel] = useState('');
    const [diarization, setDiarization] = useState(false);
    const [timestamp, setTimestamp] = useState(false);
    const [minSpeakers, setMinSpeakers] = useState('');
    const [maxSpeakers, setMaxSpeakers] = useState('');
    const [speakerNames, setSpeakerNames] = useState('');
    const [initialPrompt, setInitialPrompt] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [toast, setToast] = useState(null);

    const fileInputRef = useRef(null);

    // --- Optionen einmalig laden -------------------------------------------
    useEffect(() => {
        const controller = new AbortController();
        fetchOptions(controller.signal)
            .then((loaded) => {
                setOptions(loaded);
                setOptionsError(null);
                setOutputType((current) => current || loaded.defaultFormat);
                setLanguage((current) => current || loaded.defaultLanguage);
                setModel((current) => current || loaded.defaultModel);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') setOptionsError(error.message);
            });
        return () => controller.abort();
    }, []);

    // --- Status und Statistik pollen ---------------------------------------
    useEffect(() => {
        const controller = new AbortController();

        const poll = async () => {
            try {
                setStatus(await fetchStatus(controller.signal));
                setStatusError(null);
            } catch (error) {
                if (error.name === 'AbortError') return;
                setStatusError(error.message);
            }
            try {
                setStats(await fetchStats(controller.signal));
            } catch (error) {
                if (error.name !== 'AbortError') setStats(null);
            }
        };

        poll();
        const intervalId = setInterval(poll, TRANSCRIPTION_POLL_MS);
        return () => {
            controller.abort();
            clearInterval(intervalId);
        };
    }, []);

    const selectedFormat = useMemo(
        () => options?.formats.find((f) => f.key === outputType) || null,
        [options, outputType]
    );
    const selectedModel = useMemo(
        () => options?.models.find((m) => m.key === model) || null,
        [options, model]
    );

    const timestampForced = Boolean(selectedFormat?.needsTimestamps);
    useEffect(() => {
        if (timestampForced) setTimestamp(true);
    }, [timestampForced]);

    const maxUploadBytes = options?.limits.maxUploadMb
        ? options.limits.maxUploadMb * 1024 * 1024
        : null;
    const maxBatchFiles = options?.limits.maxBatchFiles || 50;
    const batchesAvailable = options?.features.batches !== false;

    // --- Dateiauswahl -------------------------------------------------------
    const tooLarge = useCallback(
        (candidate) => maxUploadBytes && candidate.size > maxUploadBytes,
        [maxUploadBytes]
    );

    const acceptFiles = useCallback(
        (list) => {
            const candidates = Array.from(list || []).filter(Boolean);
            if (!candidates.length) return;

            const oversized = candidates.filter(tooLarge);
            const usable = candidates.filter((c) => !tooLarge(c));

            if (oversized.length) {
                setToast({
                    tone: 'error',
                    message: `${oversized.length === 1 ? `„${oversized[0].name}" überschreitet` : `${oversized.length} Dateien überschreiten`} das Limit von ${options.limits.maxUploadMb} MB.`,
                });
            }
            if (!usable.length) return;

            if (mode === 'single') {
                const candidate = usable[0];
                setFile(candidate);
                setAttachmentName((current) => current || suggestName(candidate.name));
                return;
            }

            setEntries((current) => {
                const free = maxBatchFiles - current.length;
                if (free <= 0) {
                    setToast({
                        tone: 'error',
                        message: `Ein Sammelauftrag fasst höchstens ${maxBatchFiles} Dateien.`,
                    });
                    return current;
                }
                if (usable.length > free) {
                    setToast({
                        tone: 'error',
                        message: `Nur ${free} weitere ${free === 1 ? 'Datei passt' : 'Dateien passen'} in den Sammelauftrag (max. ${maxBatchFiles}).`,
                    });
                }
                return [...current, ...usable.slice(0, free).map(makeEntry)];
            });
        },
        [mode, maxBatchFiles, options, tooLarge]
    );

    const handleDrop = (event) => {
        event.preventDefault();
        setDragActive(false);
        acceptFiles(event.dataTransfer.files);
    };

    const switchMode = (next) => {
        setMode(next);
        setBatchResult(null);
        setFile(null);
        setAttachmentName('');
        setEntries([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const resetAfterUpload = () => {
        setFile(null);
        setAttachmentName('');
        setEntries([]);
        setDiarization(false);
        setTimestamp(false);
        setMinSpeakers('');
        setMaxSpeakers('');
        setSpeakerNames('');
        setInitialPrompt('');
        setBatchLabel('');
        if (options) {
            setOutputType(options.defaultFormat);
            setLanguage(options.defaultLanguage);
            setModel(options.defaultModel);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    /** Einstellungen, die für alle Dateien eines Auftrags gelten. */
    const sharedFields = () => ({
        email,
        outputType,
        diarization,
        timestamp: timestamp || timestampForced,
        language,
        model,
        initialPrompt,
        minSpeakers,
        maxSpeakers,
        speakerNames,
        maxqda: Boolean(selectedFormat?.maxqda),
    });

    const submitSingle = async () => {
        const nameProblem = nameErrorFor(attachmentName);
        if (!file) return setToast({ tone: 'error', message: 'Bitte wählen Sie eine Datei aus.' });
        if (nameProblem) return setToast({ tone: 'error', message: nameProblem });

        setIsUploading(true);
        try {
            await uploadFile({ ...sharedFields(), file, attachmentName });
            setSessionUsage((current) => ({
                uploads: current.uploads + 1,
                bytes: current.bytes + file.size,
                lastAt: Date.now(),
            }));
            setToast({ tone: 'success', message: 'Datei eingereicht. Das Ergebnis kommt per E-Mail.' });
            resetAfterUpload();
        } catch (error) {
            setToast({ tone: 'error', message: error.message || 'Fehler beim Hochladen der Datei.' });
        } finally {
            setIsUploading(false);
        }
    };

    const submitBatch = async () => {
        if (!entries.length) {
            return setToast({ tone: 'error', message: 'Bitte wählen Sie mindestens eine Datei aus.' });
        }
        if (entries.some((entry) => nameErrorFor(entry.name))) {
            return setToast({ tone: 'error', message: 'Bitte die markierten Dateinamen korrigieren.' });
        }

        setIsUploading(true);
        setBatchResult(null);
        const shared = sharedFields();
        const pending = entries.filter((entry) => entry.state !== 'done');

        try {
            const { batchId } = await createBatch({ email, label: batchLabel });
            let done = 0;
            let failed = 0;
            let bytes = 0;

            // Nacheinander hochladen: die Dateien sind gross und die API
            // verarbeitet ohnehin seriell.
            for (const entry of pending) {
                setEntries((current) =>
                    current.map((e) => (e.id === entry.id ? { ...e, state: 'uploading', error: null } : e))
                );
                try {
                    await uploadFile({
                        ...shared,
                        file: entry.file,
                        attachmentName: entry.name,
                        batchId,
                    });
                    done += 1;
                    bytes += entry.file.size;
                    setEntries((current) =>
                        current.map((e) => (e.id === entry.id ? { ...e, state: 'done' } : e))
                    );
                } catch (error) {
                    failed += 1;
                    setEntries((current) =>
                        current.map((e) =>
                            e.id === entry.id
                                ? { ...e, state: 'error', error: error.message || 'Upload fehlgeschlagen' }
                                : e
                        )
                    );
                }
            }

            // Auch bei Teilfehlern schliessen, sonst bleibt der Auftrag offen.
            let closeError = null;
            try {
                await closeBatch(batchId);
            } catch (error) {
                closeError = error.message;
            }

            setBatchResult({ batchId, done, failed, closeError });
            if (done) {
                setSessionUsage((current) => ({
                    uploads: current.uploads + done,
                    bytes: current.bytes + bytes,
                    lastAt: Date.now(),
                }));
            }
            setToast(
                failed
                    ? { tone: 'error', message: `${done} von ${pending.length} Dateien eingereicht, ${failed} fehlgeschlagen.` }
                    : { tone: 'success', message: `${done} Dateien eingereicht. Die Ergebnisse kommen per E-Mail.` }
            );
        } catch (error) {
            setToast({
                tone: 'error',
                message: error.message || 'Der Sammelauftrag konnte nicht angelegt werden.',
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!email) return setToast({ tone: 'error', message: 'Bitte geben Sie eine E-Mail-Adresse an.' });
        return mode === 'batch' ? submitBatch() : submitSingle();
    };

    // --- Anzeige ------------------------------------------------------------
    const serviceState = statusError
        ? { tone: 'error', label: 'Nicht erreichbar' }
        : !status
          ? { tone: 'idle', label: 'Lädt…' }
          : status.processing
            ? { tone: 'busy', label: 'Transkribiert' }
            : { tone: 'ok', label: 'Bereit' };

    const queueStats = [
        { label: 'Warteschlange', value: status ? formatNumber(status.queueSize) : '–' },
        { label: 'Aktueller Job', value: status?.processing ? 'läuft' : 'keiner' },
        { label: 'Modell', value: status?.model || '–' },
        {
            label: 'GPU frei',
            value: status?.gpu.freeMib ? `${formatNumber(status.gpu.freeMib)} MiB` : '–',
            hint: status && !status.gpu.usable ? 'GPU nicht nutzbar' : undefined,
        },
        // /status.jobs zaehlt je Zustand und variiert - dynamisch anhaengen.
        ...Object.entries(status?.jobs || {}).map(([key, value]) => ({
            label: JOB_STATE_LABELS[key] || key,
            value: formatNumber(value),
        })),
        { label: 'Eigene Uploads', value: formatNumber(sessionUsage.uploads) },
        { label: 'Zuletzt', value: sessionUsage.lastAt ? formatTime(sessionUsage.lastAt) : '–' },
    ];

    const serviceStats = [
        { label: 'Aufträge gesamt', value: formatNumber(stats?.jobsTotal) },
        { label: 'Erfolgsquote', value: formatPercent(stats?.successRate) },
        { label: 'Audiomaterial', value: formatHours(stats?.audioHours) },
        { label: 'Rechenzeit', value: formatHours(stats?.processingHours) },
        {
            label: 'Echtzeitfaktor',
            value: stats?.realtimeFactor ? `${stats.realtimeFactor.toFixed(2)}×` : '–',
        },
        { label: 'Mit Sprechern', value: formatPercent(stats?.diarizedShare) },
    ];

    const totalBytes = entries.reduce((sum, entry) => sum + entry.file.size, 0);

    return (
        <div className="stack">
            <Toast toast={toast} onDismiss={() => setToast(null)} />

            <section className="card">
                <div className="card-head">
                    <h2>Transkriptionsservice</h2>
                    <StatusPill
                        tone={serviceState.tone}
                        label={serviceState.label}
                        title={statusError || undefined}
                    />
                </div>
                <p>
                    Interviews werden auf den LFO-Servern transkribiert, das Ergebnis kommt per E-Mail und
                    die Datei wird anschließend vom Server entfernt. Viele Audio- und Videoformate werden
                    akzeptiert, alle Uploads werden lokal auf Viren geprüft.
                </p>
                <ul className="facts">
                    <li>
                        <strong>Dauer</strong> Wenige Minuten
                    </li>
                    <li>
                        <strong>Datenschutz</strong> Keine Weitergabe an Dritte, Verarbeitung nur lokal
                    </li>
                    {options?.limits.maxUploadMb && (
                        <li>
                            <strong>Maximale Größe</strong> {formatNumber(options.limits.maxUploadMb)} MB
                            je Datei
                        </li>
                    )}
                </ul>
            </section>

            <form className="card" onSubmit={handleSubmit}>
                <div className="card-head">
                    <h3 className="card-title">
                        {mode === 'batch' ? 'Sammelauftrag' : 'Datei hochladen'}
                    </h3>
                    {batchesAvailable && (
                        <div className="segmented" role="group" aria-label="Art des Auftrags">
                            <button
                                type="button"
                                className={mode === 'single' ? 'active' : ''}
                                onClick={() => switchMode('single')}
                                disabled={isUploading}
                            >
                                Einzeldatei
                            </button>
                            <button
                                type="button"
                                className={mode === 'batch' ? 'active' : ''}
                                onClick={() => switchMode('batch')}
                                disabled={isUploading}
                            >
                                Mehrere Dateien
                            </button>
                        </div>
                    )}
                </div>

                {optionsError && (
                    <p className="notice notice-error">
                        Die Einstellungen konnten nicht geladen werden ({optionsError}). Bitte später
                        erneut versuchen.
                    </p>
                )}

                {mode === 'batch' && (
                    <p className="notice notice-queue">
                        Alle Einstellungen unten gelten für den gesamten Auftrag. Die Dateien werden
                        nacheinander eingereicht, höchstens {maxBatchFiles} Stück.
                    </p>
                )}

                <div
                    className={`dropzone${dragActive ? ' dropzone-active' : ''}${
                        file || entries.length ? ' dropzone-filled' : ''
                    }`}
                    onDragOver={(event) => {
                        event.preventDefault();
                        setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            fileInputRef.current?.click();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,audio/*"
                        multiple={mode === 'batch'}
                        onChange={(event) => acceptFiles(event.target.files)}
                        hidden
                    />
                    {mode === 'batch' ? (
                        <>
                            <span className="dropzone-name">
                                {entries.length
                                    ? `${entries.length} ${entries.length === 1 ? 'Datei' : 'Dateien'} ausgewählt`
                                    : 'Dateien hierher ziehen oder klicken'}
                            </span>
                            <span className="dropzone-hint">
                                {entries.length
                                    ? `${formatBytes(totalBytes)} gesamt · klicken zum Hinzufügen`
                                    : `Mehrfachauswahl möglich, bis zu ${maxBatchFiles} Dateien`}
                            </span>
                        </>
                    ) : file ? (
                        <>
                            <span className="dropzone-name">{file.name}</span>
                            <span className="dropzone-hint">
                                {formatBytes(file.size)} · zum Ersetzen klicken
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="dropzone-name">Datei hierher ziehen oder klicken</span>
                            <span className="dropzone-hint">Audio- und Videoformate</span>
                        </>
                    )}
                </div>

                {mode === 'batch' && (
                    <FileQueue
                        entries={entries}
                        busy={isUploading}
                        nameError={nameErrorFor}
                        onRename={(id, value) =>
                            setEntries((current) =>
                                current.map((e) => (e.id === id ? { ...e, name: value } : e))
                            )
                        }
                        onRemove={(id) => setEntries((current) => current.filter((e) => e.id !== id))}
                    />
                )}

                {batchResult && (
                    <p className={`notice ${batchResult.failed ? 'notice-error' : 'notice-queue'}`}>
                        Sammelauftrag <code>{batchResult.batchId}</code>: {batchResult.done} eingereicht
                        {batchResult.failed ? `, ${batchResult.failed} fehlgeschlagen` : ''}.
                        {batchResult.closeError &&
                            ` Hinweis: Abschluss meldete „${batchResult.closeError}".`}
                    </p>
                )}

                <div className="field-row">
                    {mode === 'single' ? (
                        <div className="field">
                            <label htmlFor="attachment-name">Dateiname</label>
                            <input
                                id="attachment-name"
                                type="text"
                                value={attachmentName}
                                onChange={(event) => setAttachmentName(event.target.value)}
                                placeholder="Interview_01"
                                aria-invalid={Boolean(attachmentName && nameErrorFor(attachmentName))}
                            />
                            {attachmentName && nameErrorFor(attachmentName) && (
                                <p className="field-error">{nameErrorFor(attachmentName)}</p>
                            )}
                        </div>
                    ) : (
                        <div className="field">
                            <label htmlFor="batch-label">Bezeichnung des Auftrags</label>
                            <input
                                id="batch-label"
                                type="text"
                                value={batchLabel}
                                onChange={(event) => setBatchLabel(event.target.value)}
                                placeholder="Transkriptionen"
                            />
                            <p className="field-hint">Erscheint in der Sammelmail.</p>
                        </div>
                    )}

                    <div className="field">
                        <label htmlFor="email">E-Mail für das Ergebnis</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            placeholder="name@tu-dortmund.de"
                            autoComplete="email"
                        />
                        {options?.features.allowedMailDomains && (
                            <p className="field-hint">
                                Erlaubt: {options.features.allowedMailDomains.join(', ')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="field-row">
                    <div className="field">
                        <label htmlFor="output-type">Ausgabeformat</label>
                        <select
                            id="output-type"
                            value={outputType}
                            onChange={(event) => setOutputType(event.target.value)}
                            disabled={!options}
                        >
                            {(options?.formats || []).map((format) => (
                                <option key={format.key} value={format.key}>
                                    {format.label}
                                </option>
                            ))}
                        </select>
                        {selectedFormat?.description && (
                            <p className="field-hint">{selectedFormat.description}</p>
                        )}
                    </div>

                    <div className="field">
                        <label htmlFor="language">Sprache</label>
                        <select
                            id="language"
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                            disabled={!options}
                        >
                            {(options?.languages || []).map((entry) => (
                                <option key={entry.code} value={entry.code}>
                                    {entry.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="model">Modell</label>
                    <select
                        id="model"
                        value={model}
                        onChange={(event) => setModel(event.target.value)}
                        disabled={!options}
                    >
                        {(options?.models || []).map((entry) => (
                            <option key={entry.key} value={entry.key}>
                                {entry.label}
                                {entry.key === options?.defaultModel ? ' (Standard)' : ''}
                            </option>
                        ))}
                    </select>
                    {selectedModel?.description && (
                        <p className="field-hint">
                            {selectedModel.description}
                            {selectedModel.community && ' · Community-Konvertierung'}
                        </p>
                    )}
                </div>

                <fieldset className="field options">
                    <legend>Optionen</legend>
                    {options?.features.diarization !== false && (
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={diarization}
                                onChange={(event) => setDiarization(event.target.checked)}
                            />
                            <span>Sprecher erkennen</span>
                        </label>
                    )}
                    {options?.features.timestamps !== false && (
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={timestamp || timestampForced}
                                disabled={timestampForced}
                                onChange={(event) => setTimestamp(event.target.checked)}
                            />
                            <span>
                                Zeitstempel einfügen
                                {timestampForced && ' · für Untertitel erforderlich'}
                            </span>
                        </label>
                    )}
                </fieldset>

                {diarization && (
                    <div className="field-row">
                        <div className="field">
                            <label htmlFor="min-speakers">Sprecher mindestens</label>
                            <input
                                id="min-speakers"
                                type="number"
                                min={1}
                                max={options?.limits.maxSpeakers || undefined}
                                value={minSpeakers}
                                onChange={(event) => setMinSpeakers(event.target.value)}
                                placeholder="automatisch"
                            />
                        </div>
                        <div className="field">
                            <label htmlFor="max-speakers">Sprecher höchstens</label>
                            <input
                                id="max-speakers"
                                type="number"
                                min={1}
                                max={options?.limits.maxSpeakers || undefined}
                                value={maxSpeakers}
                                onChange={(event) => setMaxSpeakers(event.target.value)}
                                placeholder="automatisch"
                            />
                        </div>
                    </div>
                )}

                {diarization && options?.features.speakerNames && (
                    <div className="field">
                        <label htmlFor="speaker-names">Sprechernamen</label>
                        <input
                            id="speaker-names"
                            type="text"
                            value={speakerNames}
                            onChange={(event) => setSpeakerNames(event.target.value)}
                            placeholder="Interviewer, Befragte"
                        />
                        <p className="field-hint">
                            Kommagetrennt, in der Reihenfolge des ersten Auftretens
                            {mode === 'batch' && ' – gilt für alle Dateien'}.
                        </p>
                    </div>
                )}

                {options?.features.initialPrompt && (
                    <>
                        <button
                            type="button"
                            className="link-btn disclosure"
                            onClick={() => setShowAdvanced((value) => !value)}
                            aria-expanded={showAdvanced}
                        >
                            {showAdvanced ? 'Erweitert ausblenden' : 'Erweiterte Einstellungen'}
                        </button>
                        {showAdvanced && (
                            <div className="field">
                                <label htmlFor="initial-prompt">Initial-Prompt</label>
                                <textarea
                                    id="initial-prompt"
                                    rows={3}
                                    value={initialPrompt}
                                    onChange={(event) => setInitialPrompt(event.target.value)}
                                    placeholder="Fachbegriffe, Eigennamen oder Schreibweisen, die im Interview vorkommen"
                                />
                                <p className="field-hint">
                                    Hilft dem Modell bei Fachbegriffen und Eigennamen.
                                </p>
                            </div>
                        )}
                    </>
                )}

                <button type="submit" className="btn btn-primary btn-block" disabled={isUploading}>
                    {isUploading
                        ? 'Wird eingereicht…'
                        : mode === 'batch'
                          ? `${entries.length || ''} ${entries.length === 1 ? 'Datei' : 'Dateien'} einreichen`.trim()
                          : 'Hochladen'}
                </button>
            </form>

            <section className="card">
                <h3 className="card-title">Auslastung</h3>
                <StatGrid items={queueStats} />
                {status?.active?.length > 0 && (
                    <p className="card-note">Aktive Aufträge: {status.active.length}</p>
                )}
                {statusError && (
                    <p className="card-note card-note-error">Status nicht abrufbar: {statusError}</p>
                )}
            </section>

            <section className="card">
                <div className="card-head">
                    <h3 className="card-title">Servicestatistik</h3>
                    {!stats && <StatusPill tone="queued" label="nicht verfügbar" />}
                </div>
                <StatGrid items={serviceStats} />
                {stats?.privacy && <p className="card-note">{stats.privacy}</p>}
            </section>
        </div>
    );
}

export default TranscriptionTool;
